import { useState, useEffect, useCallback } from "react";
import { T, F, Over, Pill, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// O. Reg. 567/05 s.20 + 579/05 ss.15–16: non-cash property in trust register.
// Bricks does NOT notify RECO or replace the property.
const day = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { month:'short', day:'numeric', year:'numeric' }) : '—';
const STATUS = {
  held: ['ok', 'Held'],
  partially_withdrawn: ['warn', 'Partially withdrawn'],
  withdrawn: ['neutral', 'Withdrawn'],
  missing: ['brick', 'Missing'],
  replaced: ['neutral', 'Replaced'],
};

const EMPTY_HOLDING = {
  description: '', received_at: '', received_from: '', on_behalf_of: '', purpose: '',
  received_by_name: '', storage_location: '', real_estate: '', estimated_value: '', notes: '',
};
const EMPTY_WD = { description: '', withdrawn_at: '', withdrawn_to: '', purpose: '', real_estate: '', is_full: true };
const EMPTY_NOTIFY = { notify_method: 'phone', notify_reference: '', notes: '' };

export default function TrustProperty({ BASE, headers, p, dark, canManage, canAuth }) {
  const [data, setData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [withdrawFor, setWithdrawFor] = useState(null);
  const [missingFor, setMissingFor] = useState(null);
  const [notifyFor, setNotifyFor] = useState(null);
  const [replaceFor, setReplaceFor] = useState(null);
  const [form, setForm] = useState(EMPTY_HOLDING);
  const [wd, setWd] = useState(EMPTY_WD);
  const [missingDesc, setMissingDesc] = useState('');
  const [notify, setNotify] = useState(EMPTY_NOTIFY);
  const [replaceNotes, setReplaceNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    fetch(`${BASE}/api/accounting/trust/property`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData(null));
  }, [BASE, headers.Authorization]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const post = async (path, body, ok) => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/api/accounting/trust/property${path}`, {
        method: 'POST', headers, body: JSON.stringify(body || {}),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'That didn’t go through');
      if (ok) toast.success(ok);
      load();
      return d;
    } catch (e) { toast.error(e.message); return null; }
    finally { setBusy(false); }
  };

  const openDetail = async (id) => {
    try {
      const res = await fetch(`${BASE}/api/accounting/trust/property/${id}`, { headers });
      const d = await res.json().catch(() => null);
      if (res.ok) setDetail(d);
    } catch { /* ignore */ }
  };

  const rows = data?.holdings || [];
  const heldCount = rows.filter(r => r.status === 'held' || r.status === 'partially_withdrawn').length;
  const input = {
    marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`,
    borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg,
    outline: 'none', boxSizing: 'border-box',
  };
  const small = (color) => ({
    background: 'none', border: `1px solid ${p.hair}`, borderRadius: 2, padding: '4px 10px',
    cursor: 'pointer', fontSize: 11, fontFamily: F.mono, color,
  });
  const field = (label, key, state, setState, extra = {}) => (
    <div>
      <Over color={p.ink3}>{label}</Over>
      {extra.select ? (
        <select style={input} value={state[key]} onChange={e => setState({ ...state, [key]: e.target.value })}>
          {extra.select.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : extra.textarea ? (
        <textarea style={{ ...input, minHeight: 72, resize: 'vertical' }} value={state[key]}
          onChange={e => setState({ ...state, [key]: e.target.value })} placeholder={extra.placeholder || ''} />
      ) : (
        <input style={input} type={extra.type || 'text'} value={state[key]}
          onChange={e => setState({ ...state, [key]: e.target.value })} placeholder={extra.placeholder || ''} />
      )}
    </div>
  );

  if (!data) return null;

  return (
    <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2 }}>
      <div style={{ padding: '16px var(--bx-pad)', borderBottom: `1px solid ${p.hair}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <Over color={p.ink3}>Property in trust · O. Reg. 567/05 s.20 · 579/05 ss.15–16</Over>
          <div style={{ fontSize: 13, color: p.ink2, marginTop: 4, maxWidth: 640 }}>
            Non-cash deposits and other property — preserve safely; the broker of record authorizes every withdrawal.
            If missing, record that RECO was notified — Bricks does not send the notice or replace the property for you.
            {' '}{heldCount ? `${heldCount} held` : 'None held'}.
          </div>
        </div>
        {canManage && (
          <Btn variant="ghost" dark={dark} disabled={busy} onClick={() => { setForm(EMPTY_HOLDING); setShowCreate(true); }}>
            Record receipt
          </Btn>
        )}
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: '24px var(--bx-pad)', fontSize: 13, color: p.ink3 }}>
          No non-cash property recorded. Keys, remotes, documents or other items held for a deal go here — not money in the Real Estate Trust Account.
        </div>
      ) : (
        rows.slice(0, 30).map((r, i) => {
          const [tone, label] = STATUS[r.status] || ['neutral', r.status];
          return (
            <div key={r.id} style={{
              display: 'grid', gridTemplateColumns: '1.6fr 1fr 1.1fr 1fr auto', gap: 12,
              padding: '12px var(--bx-pad)', borderBottom: i === Math.min(rows.length, 30) - 1 ? 'none' : `1px solid ${p.hair}`,
              fontSize: 13, alignItems: 'center',
            }}>
              <div>
                <div style={{ color: p.ink, cursor: 'pointer' }} onClick={() => openDetail(r.id)}>{r.description}</div>
                <div style={{ fontSize: 11, color: p.ink3, marginTop: 2 }}>
                  From {r.received_from}{r.on_behalf_of ? ` for ${r.on_behalf_of}` : ''} · {day(r.received_at)}
                  {r.real_estate ? ` · ${r.real_estate}` : ''}
                </div>
              </div>
              <div><Pill tone={tone} dark={dark}>{label}</Pill></div>
              <div style={{ fontSize: 12, color: p.ink3 }}>
                {r.storage_location || '—'}
                {r.withdrawal_count ? ` · ${r.withdrawal_count} withdrawal${r.withdrawal_count === 1 ? '' : 's'}` : ''}
                {r.open_missing_count ? ` · missing notice open` : ''}
              </div>
              <div style={{ fontSize: 12, color: p.ink2 }}>{r.purpose}</div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {canAuth && (r.status === 'held' || r.status === 'partially_withdrawn') && (
                  <button disabled={busy} style={small(T.brick)} onClick={() => {
                    setWithdrawFor(r); setWd({ ...EMPTY_WD, real_estate: r.real_estate || '', description: 'all' });
                  }}>Withdraw</button>
                )}
                {canManage && (r.status === 'held' || r.status === 'partially_withdrawn') && (
                  <button disabled={busy} style={small(T.bad)} onClick={() => {
                    setMissingFor(r); setMissingDesc(r.description || '');
                  }}>Report missing</button>
                )}
                {canAuth && r.open_missing_count > 0 && r.status === 'missing' && (
                  <button disabled={busy} style={small(T.brick)} onClick={async () => {
                    const d = await fetch(`${BASE}/api/accounting/trust/property/${r.id}`, { headers }).then(x => x.json()).catch(() => null);
                    const open = (d?.missing_notices || []).find(n => n.status === 'open');
                    const notified = (d?.missing_notices || []).find(n => n.status === 'notified');
                    if (open) { setNotifyFor(open); setNotify(EMPTY_NOTIFY); }
                    else if (notified) { setReplaceFor(notified); setReplaceNotes(''); }
                    else toast.error('No open missing notice on this holding');
                  }}>Record RECO / replaced</button>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Create */}
      <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
        <DialogContent style={{ maxWidth: 520, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Record property received</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Not money — this does not touch the Real Estate Trust Account ledger. O. Reg. 579/05 s.15.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 18 }}>
            {field('Description', 'description', form, setForm, { placeholder: 'e.g. Two garage remotes for 1 Maple St' })}
            {field('Received on', 'received_at', form, setForm, { type: 'date' })}
            {field('Received from', 'received_from', form, setForm)}
            {field('On behalf of (optional)', 'on_behalf_of', form, setForm)}
            {field('Purpose', 'purpose', form, setForm)}
            {field('Received by (broker / salesperson)', 'received_by_name', form, setForm)}
            {field('Storage location (optional)', 'storage_location', form, setForm, { placeholder: 'e.g. Office safe' })}
            {field('Real estate (optional)', 'real_estate', form, setForm)}
            {field('Estimated value CAD (optional, informational only)', 'estimated_value', form, setForm, { placeholder: 'e.g. 25.00' })}
            {field('Notes (optional)', 'notes', form, setForm)}
          </div>
          <DialogFooter style={{ marginTop: 22, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowCreate(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={async () => {
              const body = { ...form };
              if (body.estimated_value !== '' && body.estimated_value != null) {
                const n = Math.round(parseFloat(String(body.estimated_value).replace(/[$,]/g, '')) * 100);
                body.estimated_value_cents = Number.isFinite(n) ? n : undefined;
              }
              delete body.estimated_value;
              const ok = await post('', body, 'Receipt recorded');
              if (ok) setShowCreate(false);
            }}>Save receipt</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw */}
      <Dialog open={!!withdrawFor} onOpenChange={(o) => !o && setWithdrawFor(null)}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Authorize withdrawal</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Only the broker of record (or designated alternate) can authorize a withdrawal under O. Reg. 567/05 s.20(2).
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 18 }}>
            {field('What is withdrawn', 'description', wd, setWd, { placeholder: 'all / one remote / …' })}
            {field('Withdrawn on', 'withdrawn_at', wd, setWd, { type: 'date' })}
            {field('Withdrawn to', 'withdrawn_to', wd, setWd)}
            {field('Purpose', 'purpose', wd, setWd)}
            {field('Real estate (optional)', 'real_estate', wd, setWd)}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: p.ink2, marginTop: 4 }}>
              <input type="checkbox" checked={!!wd.is_full} onChange={e => setWd({ ...wd, is_full: e.target.checked })} />
              Full withdrawal (nothing left held)
            </label>
          </div>
          <DialogFooter style={{ marginTop: 22, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setWithdrawFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={async () => {
              const ok = await post(`/${withdrawFor.id}/withdrawals`, wd, 'Withdrawal recorded');
              if (ok) setWithdrawFor(null);
            }}>Authorize &amp; save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report missing */}
      <Dialog open={!!missingFor} onOpenChange={(o) => !o && setMissingFor(null)}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Report missing property</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              O. Reg. 579/05 s.16 requires immediate notice to the registrar and replacement. Bricks only records that you did so — it does not contact RECO.
            </DialogDescription>
          </DialogHeader>
          <div style={{ paddingTop: 18 }}>
            <Over color={p.ink3}>What is missing</Over>
            <input style={input} value={missingDesc} onChange={e => setMissingDesc(e.target.value)} />
          </div>
          <DialogFooter style={{ marginTop: 22, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setMissingFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={async () => {
              const ok = await post(`/${missingFor.id}/missing`, { description: missingDesc }, 'Missing notice opened');
              if (ok) { setMissingFor(null); if (ok.notice) { setNotifyFor(ok.notice); setNotify(EMPTY_NOTIFY); } }
            }}>Open notice</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify RECO */}
      <Dialog open={!!notifyFor} onOpenChange={(o) => !o && setNotifyFor(null)}>
        <DialogContent style={{ maxWidth: 480, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Record RECO notification</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Bricks does not send the notice for you. Enter how you notified the registrar and the confirmation or person spoken to.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 18 }}>
            {field('Method', 'notify_method', notify, setNotify, {
              select: [['phone', 'Phone'], ['email', 'Email'], ['portal', 'RECO portal / MyWeb'], ['other', 'Other']],
            })}
            {field('Reference (who / confirmation #)', 'notify_reference', notify, setNotify, { placeholder: 'e.g. Spoke with J. Lee, case #12345' })}
            {field('Notes (optional)', 'notes', notify, setNotify)}
          </div>
          <DialogFooter style={{ marginTop: 22, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setNotifyFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={async () => {
              const ok = await post(`/missing/${notifyFor.id}/notify`, notify, 'Notification recorded');
              if (ok) { setNotifyFor(null); setReplaceFor(ok); setReplaceNotes(''); }
            }}>Save record</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replaced */}
      <Dialog open={!!replaceFor} onOpenChange={(o) => !o && setReplaceFor(null)}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Record replacement</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Confirm the missing property was replaced. If you received a new item to hold, also record a new receipt.
            </DialogDescription>
          </DialogHeader>
          <div style={{ paddingTop: 18 }}>
            <Over color={p.ink3}>Notes (optional)</Over>
            <input style={input} value={replaceNotes} onChange={e => setReplaceNotes(e.target.value)} />
          </div>
          <DialogFooter style={{ marginTop: 22, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setReplaceFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={async () => {
              const ok = await post(`/missing/${replaceFor.id}/replaced`, { notes: replaceNotes }, 'Replacement recorded');
              if (ok) setReplaceFor(null);
            }}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent style={{ maxWidth: 560, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>
              {detail?.holding?.description || 'Holding'}
            </DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              {detail?.honest_note || data?.honest_note}
            </DialogDescription>
          </DialogHeader>
          {detail?.holding && (
            <div style={{ paddingTop: 14, fontSize: 13, color: p.ink2, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>Status: {detail.holding.status} · Received {day(detail.holding.received_at)} from {detail.holding.received_from}</div>
              <div>Purpose: {detail.holding.purpose}</div>
              {detail.holding.storage_location && <div>Stored: {detail.holding.storage_location}</div>}
              <div style={{ marginTop: 10 }}>
                <Over color={p.ink3}>Withdrawals</Over>
                {(detail.withdrawals || []).length === 0 ? <div style={{ color: p.ink3, marginTop: 4 }}>None</div> : (
                  detail.withdrawals.map(w => (
                    <div key={w.id} style={{ marginTop: 6, fontSize: 12 }}>
                      {day(w.withdrawn_at)} · {w.description} → {w.withdrawn_to} · authorized by {w.authorized_by_name}
                    </div>
                  ))
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <Over color={p.ink3}>Missing notices</Over>
                {(detail.missing_notices || []).length === 0 ? <div style={{ color: p.ink3, marginTop: 4 }}>None</div> : (
                  detail.missing_notices.map(m => (
                    <div key={m.id} style={{ marginTop: 6, fontSize: 12 }}>
                      {m.status} · {m.description}
                      {m.notified_at ? ` · notified ${day(m.notified_at)} via ${m.notify_method}` : ''}
                      {m.replaced_at ? ` · replaced ${day(m.replaced_at)}` : ''}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          <DialogFooter style={{ marginTop: 22 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setDetail(null)}>Close</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
